import { ReactNode } from "react";
import { StandardGradientHeader } from "@/util-components/dialog/components/StandardGradientHeader";
import { Calendar, Pencil } from "lucide-react";
import { formatDate } from "@/lib/utils/date-formatter";
import { cn } from "@/lib/utils/cn";

/**
 * A standardized header section for details dialogs.
 * Uses a dark gradient background and provides consistent padding.
 */
export const DialogHeaderSection = ({ children, className }: { children: ReactNode, className?: string }) => {
  return (
    <StandardGradientHeader className={className}>
      {children}
    </StandardGradientHeader>
  );
};

/**
 * A wrapper for content sections inside the details dialog.
 * Displays a small uppercase title with a primary icon and optional right-aligned elements.
 */
export const DialogContentSection = ({ 
  title, 
  icon: Icon, 
  children,
  rightElement
}: { 
  /** The title of the section */
  title: string; 
  /** The Lucide icon to display next to the title */
  icon: any; 
  /** The content to display within the section */
  children: ReactNode;
  /** Optional element to render on the far right of the section header (e.g. Badges) */
  rightElement?: ReactNode;
}) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-500" />
          {title}
        </h3>
        {rightElement}
      </div>
      {children}
    </div>
  );
};

/**
 * A standardized data display item, typically used inside a CSS Grid.
 * Displays an icon next to a label and the actual value in a clean, flat style.
 */
export const DialogContentItem = ({
  label,
  value,
  icon: Icon
}: {
  /** The label describing the value */
  label: string;
  /** The main value to display (can be text or a ReactNode) */
  value: ReactNode;
  /** The Lucide icon to display next to the label */
  icon: any;
}) => {
  return (
    <div className="bg-white border border-gray-200/60 rounded-xl p-4 sm:p-5 flex items-start gap-3.5 transition-colors hover:bg-gray-50/50">
      <div className="mt-0.5 shrink-0 text-gray-400">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex flex-col min-w-0 gap-1">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        <div className="text-sm font-semibold text-gray-900 break-words">{value}</div>
      </div>
    </div>
  );
};

/**
 * A standardized section for displaying metadata like creation and update dates.
 * Typically placed at the very bottom of the details view.
 */
export const DialogMetaSection = ({
  createdAt,
  updatedAt
}: {
  createdAt: string | Date;
  updatedAt: string | Date;
}) => {
  return (
    <div className="mt-6 px-1">
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-400">
        <span className="flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5" />
          Erstellt: {formatDate(createdAt instanceof Date ? createdAt.toISOString() : createdAt)}
        </span>
        <span className="flex items-center gap-1.5">
          <Pencil className="w-3.5 h-3.5" />
          Aktualisiert: {formatDate(updatedAt instanceof Date ? updatedAt.toISOString() : updatedAt)}
        </span>
      </div>
    </div>
  );
};

/**
 * A standardized section for displaying multi-line free text (e.g., biographies, post captions, descriptions).
 */
export const DialogTextSection = ({
  title,
  icon,
  text,
  rightElement
}: {
  title: string;
  icon: any;
  text: string | ReactNode;
  rightElement?: ReactNode;
}) => {
  return (
    <DialogContentSection title={title} icon={icon} rightElement={rightElement}>
      <div className="bg-white border border-gray-200/60 rounded-xl p-4 sm:p-5">
        <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{text}</div>
      </div>
    </DialogContentSection>
  );
};

/**
 * A standardized section for displaying a list of badges, tags, or small items.
 */
export const DialogTagsSection = ({
  title,
  icon,
  children,
  rightElement
}: {
  title: string;
  icon: any;
  children: ReactNode;
  rightElement?: ReactNode;
}) => {
  return (
    <DialogContentSection title={title} icon={icon} rightElement={rightElement}>
      <div className="bg-white border border-gray-200/60 rounded-xl p-4 sm:p-5">
        <div className="flex flex-wrap gap-2">
          {children}
        </div>
      </div>
    </DialogContentSection>
  );
};

/**
 * A standardized wrapper for grouping form fields inside the edit mode of details dialogs.
 * Aligned with DialogContentSection design.
 */
export const DialogFormSection = ({
  title,
  icon: Icon,
  children,
  rightElement,
  className,
}: {
  title?: string;
  icon?: any;
  children: ReactNode;
  rightElement?: ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn("space-y-3", className)}>
      {(title || Icon || rightElement) && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-gray-500" />}
            {title}
          </h3>
          {rightElement}
        </div>
      )}
      <div className="bg-white border border-gray-200/60 rounded-xl p-4 sm:p-5">
        {children}
      </div>
    </div>
  );
};

